"use client";

/**
 * Error Boundary for Page Components
 * Catches errors in page rendering and provides recovery UI
 */

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Log error to console (client-side)
    console.error("[Page Error]", {
      message: error.message,
      digest: error.digest,
      stack: error.stack,
    });

    // Capture error in Sentry with additional context
    Sentry.captureException(error, {
      tags: {
        errorType: "page_error",
        digest: error.digest,
      },
      extra: {
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
    });

    // Send error to server for logging (fire and forget)
    fetch("/api/log-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "page_error",
        message: error.message,
        digest: error.digest,
        stack: error.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
      }),
    }).catch(() => {
      // Ignore logging failures
    });
  }, [error]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
          <AlertTriangle className="size-8 text-destructive" />
        </div>

        <h1 className="text-xl font-semibold text-foreground mb-2">
          Something went wrong
        </h1>

        <p className="text-sm text-muted-foreground mb-6">
          An unexpected error occurred while loading this page.
          {error.digest && (
            <span className="block mt-2 text-xs font-mono text-muted-foreground/60">
              Error ID: {error.digest}
            </span>
          )}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={reset} icon={<RefreshCw className="size-4" />}>
            Try Again
          </Button>
          <Button
            variant="secondary"
            onClick={() => (window.location.href = "/dashboard")}
            icon={<Home className="size-4" />}
          >
            Go to Dashboard
          </Button>
        </div>

        {process.env.NODE_ENV === "development" && error.message && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
              Error details (dev only)
            </summary>
            <pre className="mt-2 p-3 bg-muted rounded-lg text-xs overflow-auto max-h-48 text-destructive">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </Card>
    </div>
  );
}
